import { useEffect, useState } from 'react';
import Search from './views/Search';
import Content from './views/Content';
import Plans from './views/Plans';
import Signup from './views/Signup';
import UserSettings from './views/UserSettings';
import Terms from './views/Terms';
import Privacy from './views/Privacy';
import MyContributions from './views/MyContributions';
import FeedbackQueueAdmin from './views/admin/FeedbackQueueAdmin';
import AnalyticsPageView from './components/AnalyticsPageView';
import AnalyticsConsentBanner from './components/AnalyticsConsentBanner';
import FeedbackButton from './components/feedback/FeedbackButton';
import AppContext from './services/context';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { useUserProfile } from './hooks/useUserProfile';

const THEME_STORAGE_KEY = 'greenbook:theme';
const DEFAULT_THEME_KEY = 'light';
const SEMANTIC_SEARCH_STORAGE_KEY = 'greenbook:semantic-search';
const DEFAULT_SEMANTIC_SEARCH = true;

function App() {
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [individualResult, setIndividualResult] = useState({});
  const [loading, setLoading] = useState(false);
  const [themeKey, setThemeKey] = useState(DEFAULT_THEME_KEY);
  const [isAiMode, setIsAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [semanticSearchEnabled, setSemanticSearchEnabled] = useState(DEFAULT_SEMANTIC_SEARCH);
  const [activeFilters, setActiveFilters] = useState({
    therapeuticEffects: [],
    categories: [],
  });
  const { state: userState, actions: userActions } = useUserProfile();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
        setThemeKey(storedTheme);
      }

      const storedSemanticPreference = window.localStorage.getItem(SEMANTIC_SEARCH_STORAGE_KEY);
      if (storedSemanticPreference === 'false') {
        setSemanticSearchEnabled(false);
      }
    }
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(themeKey);
  }, [themeKey]);

  const toggleTheme = () => {
    setThemeKey((currentKey) => {
      const nextKey = currentKey === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextKey);
      }
      return nextKey;
    });
  };

  const toggleSemanticSearch = () => {
    setSemanticSearchEnabled((currentValue) => {
      const nextValue = !currentValue;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          SEMANTIC_SEARCH_STORAGE_KEY,
          String(nextValue)
        );
      }
      return nextValue;
    });
  };

  return (
    <AppContext.Provider
      value={{
        searchResults,
        setSearchResults,
        individualResult,
        setIndividualResult,
        loading,
        setLoading,
        searchTerm,
        setSearchTerm,
        themeKey,
        toggleTheme,
        semanticSearchEnabled,
        toggleSemanticSearch,
        setSemanticSearchEnabled,
        isAiMode,
        setIsAiMode,
        aiResponse,
        setAiResponse,
        aiLoading,
        setAiLoading,
        activeFilters,
        setActiveFilters,
        userState,
        userActions,
      }}
    >
        <SnackbarProvider
          dense={true}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={3000}
        >
          <div
            className={`min-h-screen ${
              themeKey === 'light'
                ? 'bg-greenbook-light-bg text-greenbook-light-text'
                : 'bg-greenbook-dark-bg text-greenbook-dark-text'
            } transition-colors duration-300`}
          >
            <Router>
              <AnalyticsPageView />
              <Routes>
                <Route exact path="/" element={<Search />} />
                <Route exact path="/planos" element={<Plans />} />
                <Route exact path="/cadastro" element={<Signup />} />
                <Route exact path="/configuracoes" element={<UserSettings />} />
                <Route exact path="/minhas-contribuicoes" element={<MyContributions />} />
                <Route exact path="/termos" element={<Terms />} />
                <Route exact path="/politica-de-privacidade" element={<Privacy />} />
                {/* Rota antiga mantida como redirect: já circulou em links externos e no aceite de versões anteriores. */}
                <Route exact path="/privacidade" element={<Navigate to="/politica-de-privacidade" replace />} />
                <Route exact path="/admin/feedback" element={<FeedbackQueueAdmin />} />
                <Route exact path="/:type/:id" element={<Content />} />
              </Routes>
              <FeedbackButton />
              <AnalyticsConsentBanner />
            </Router>
          </div>
        </SnackbarProvider>
    </AppContext.Provider>
  );
}

export default App;
